import dayjs from 'dayjs';
import {
  getEmptyPriceLabel,
  toMoney,
  roundToOne,
  bankersRound,
  dollarsToCents,
} from '../../../utils/money';
import ProductSetItemModel from './product_set_item';
import {
  lowerProductPrice,
  lowerProductPriceMembersTag,
} from '../../../utils/price';

const PRODUCT_SET_PRICE_TYPE = {
  DISCOUNT_PERCENTAGE: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_amount',
};
const FK_ROUNDING_TO_ONE_DECIMAL = 'rounding_to_one_decimal';

class ProductSetModel {
  constructor(product) {
    this.product = product;
    this.selected = this.computedInitSelected();
    this.loading = false;
    this.error = '';
  }
  get salesStarted() {
    const currentTime = dayjs(new Date().getTime());
    const { available_start_time } = this.product;
    if (
      available_start_time &&
      currentTime.isBefore(dayjs(available_start_time))
    ) {
      return false;
    }
    return true;
  }
  get salesEnded() {
    const currentTime = dayjs(new Date().getTime());
    const { available_end_time } = this.product;
    if (available_end_time && currentTime.isAfter(dayjs(available_end_time))) {
      return true;
    }
    return false;
  }
  get availableSalesTime() {
    return this.salesStarted && !this.salesEnded;
  }
  get hasVariationsChildProduct() {
    return this.computedHasVariationsChildProduct();
  }
  get hasSufficientStock() {
    return this.computedHasSufficientStock();
  }
  get childProductsMap() {
    return this.computedChildProductsMap();
  }
  get mustSelectNum() {
    return this.computedMustSelectNum();
  }
  get currentSelectedNum() {
    return this.computedCurrentSelectedNum();
  }
  get finishSelected() {
    return this.computedFinishSelected();
  }
  get checkoutPriceMoney() {
    return this.computedCheckoutPriceMoney();
  }
  get checkoutPriceLabel() {
    return this.computedCheckoutPriceLabel();
  }
  get allowAddToCart() {
    return this.hasSufficientStock && this.availableSalesTime;
  }
  computedInitSelected() {
    const selected = {};
    this.product.child_products.forEach((childProduct) => {
      const { _id, bundle_set = {} } = childProduct;
      const { min_quantity } = bundle_set;
      const childProductModel = new ProductSetItemModel(childProduct);
      let selectedNum = 0;
      if (
        this.allowAddToCart &&
        !childProductModel.hasVariations &&
        childProductModel.hasSufficientStock
      ) {
        selectedNum = min_quantity;
      }
      selected[_id] = {
        id: _id,
        selectedNum,
        selectedVariationsNum: {},
      };
    });

    return selected;
  }
  computedHasVariationsChildProduct() {
    return !!this.product.child_products.find((childProduct) => {
      const childProductModel = new ProductSetItemModel(childProduct);
      return childProductModel.hasVariations;
    });
  }
  computedHasSufficientStock() {
    const hasSufficientStock = this.product.child_products.every(
      (childProduct) =>
        new ProductSetItemModel(childProduct).hasSufficientStock,
    );
    return hasSufficientStock;
  }
  computedChildProductsMap() {
    const childProductsMap = this.product.child_products.reduce(
      (current, child_product) => {
        current[child_product._id] = child_product;
        return current;
      },
      {},
    );
    return childProductsMap;
  }
  computedMustSelectNum() {
    return this.product.child_products.reduce((current, childProduct) => {
      const { bundle_set = {} } = childProduct;
      const { min_quantity } = bundle_set;
      return current + min_quantity;
    }, 0);
  }
  computedCurrentSelectedNum() {
    const { selected } = this;
    return Object.values(selected).reduce((current, childProductSelected) => {
      return current + (childProductSelected.selectedNum || 0);
    }, 0);
  }
  computedCheckoutPriceMoney() {
    const { finishSelected, product, selected, childProductsMap } = this;

    if (!finishSelected) {
      return null;
    }

    if (product.bundle_set.price_type === 'fixed_amount') {
      return {
        label: `${lowerProductPrice(product)} ${lowerProductPriceMembersTag(product)}`,
      };
    }

    const selectedInfo = Object.values(selected);
    let totalPrice = selectedInfo.reduce(
      (current, childProductSelectedInfo) => {
        const { id, selectedNum, selectedVariationsNum } =
          childProductSelectedInfo;

        if (!selectedNum) {
          return current;
        }

        const childProduct = childProductsMap[id];
        const childProductModel = new ProductSetItemModel(childProduct);

        const { hasVariations, variantsMap } = childProductModel;

        if (childProduct.same_price || !hasVariations) {
          const { price_sale, price } = childProduct;
          return (
            current + (price_sale?.cents || price?.cents || 0) * selectedNum
          );
        }

        const selectedVantionKeys = Object.keys(selectedVariationsNum);
        const vantionsPrice = selectedVantionKeys.reduce(
          (currentVantionsPrice, vantionKey) => {
            const value = selectedVariationsNum[vantionKey];
            const vantion = variantsMap[vantionKey];
            if (!value || !vantion) {
              return currentVantionsPrice;
            }
            const { price_sale, price } = vantion;
            return (
              currentVantionsPrice +
              (price_sale?.cents || price?.cents || 0) * value
            );
          },
          0,
        );
        return current + vantionsPrice;
      },
      0,
    );
    const { DISCOUNT_PERCENTAGE, DISCOUNT_AMOUNT } = PRODUCT_SET_PRICE_TYPE;
    let discountAmount = 0;
    if (product.bundle_set.price_type === DISCOUNT_PERCENTAGE) {
      discountAmount = (totalPrice * product.bundle_set.discount_value) / 100;
      discountAmount = bankersRound(discountAmount);
    } else if (product.bundle_set.price_type === DISCOUNT_AMOUNT) {
      discountAmount = dollarsToCents(
        product.bundle_set.discount_value,
        window.mainConfig.merchantData.base_currency.iso_code,
      );
    }
    totalPrice -= discountAmount;
    totalPrice = totalPrice > 0 ? totalPrice : 0;
    if (
      window.mainConfig.merchantData.rollout_keys.includes(
        FK_ROUNDING_TO_ONE_DECIMAL,
      )
    ) {
      totalPrice = roundToOne(totalPrice);
    }

    return toMoney({
      cents: totalPrice,
    });
  }
  computedCheckoutPriceLabel() {
    return this.checkoutPriceMoney
      ? this.checkoutPriceMoney.label
      : getEmptyPriceLabel();
  }
  computedFinishSelected() {
    const { mustSelectNum, currentSelectedNum } = this;
    return mustSelectNum === currentSelectedNum;
  }
  onSelectedChange(childProductId, variantKey, num) {
    const { selected } = this;
    const newSelected = { ...selected };
    if (!childProductId) {
      return;
    }
    const childProductSelected = newSelected[childProductId];
    const { selectedVariationsNum = {} } = childProductSelected;
    if (!variantKey) {
      childProductSelected.selectedNum = num;
    } else {
      if (num > 0) {
        selectedVariationsNum[variantKey] = num;
      } else {
        delete selectedVariationsNum[variantKey];
      }
      childProductSelected.selectedVariationsNum = selectedVariationsNum;
      childProductSelected.selectedNum = Object.values(
        selectedVariationsNum,
      ).reduce((current, vantionsNum) => {
        return current + vantionsNum;
      }, 0);
    }
    this.selected = newSelected;
  }
  computedChildProductModel(childProductId) {
    const { childProductsMap } = this;
    const childProduct = childProductsMap[childProductId];

    if (!childProduct) {
      return null;
    }

    return new ProductSetItemModel(childProduct);
  }
  computedFinishSelectForChildProduct(childProductId) {
    const { selected } = this;
    const childProductModel = this.computedChildProductModel(childProductId);

    if (!childProductModel) {
      return false;
    }
    const { selectedNum = 0 } = selected[childProductId];
    return selectedNum === childProductModel.mustSelectNum;
  }
  computedNextMustSelectChildProduct(childProductId) {
    const { product } = this;
    const { child_products } = product;
    const currentIndex = child_products.findIndex(
      (childProduct) => childProduct._id === childProductId,
    );
    const nextIndex = child_products.findIndex((childProduct, index) => {
      if (index <= currentIndex) {
        return;
      }
      const childProductModel = this.computedChildProductModel(
        childProduct._id,
      );
      const { hasVariations } = childProductModel;
      const finishSelected = this.computedFinishSelectForChildProduct(
        childProduct._id,
      );
      if (hasVariations && !finishSelected) {
        return true;
      }
    });

    return nextIndex > currentIndex ? child_products[nextIndex] : null;
  }
  updateChildProductData(childProductId, childVariationKey, data = {}) {
    const { child_products } = this.product;
    const newChildProducts = child_products.map((childProduct) => {
      if (childProduct._id !== childProductId) {
        return childProduct;
      }
      if (!childVariationKey) {
        return {
          ...childProduct,
          ...data,
        };
      }
      const newVariations = (childProduct.variations || []).map((variation) => {
        if (variation.key === childVariationKey) {
          return {
            ...variation,
            ...data,
          };
        }
        return variation;
      });
      return {
        ...childProduct,
        variations: newVariations,
      };
    });
    this.product.child_products = newChildProducts;
  }
}

export default ProductSetModel;
